import { useRouter } from "expo-router";

import { ResourceListScreen } from "~/components/composed/ResourceListScreen";
import { Button, ButtonText, Row } from "~/components/ui";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceProjects,
  useWorkspaceTasks,
  useWorkspaceTimeEntries,
} from "~/hooks/workspace";

function formatDuration(seconds: number) {
  const hours = seconds / 3600;
  return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
}

function billableValue(entry: {
  billable: boolean;
  duration_seconds: number;
  hourly_rate_cents: number | null;
}) {
  if (!entry.billable || !entry.hourly_rate_cents) return 0;
  return Math.round((entry.duration_seconds / 3600) * entry.hourly_rate_cents);
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

const BILLABLE_OPTIONS = [
  { label: "Billable", value: "true" },
  { label: "Non-billable", value: "false" },
];

export default function TimeEntriesScreen() {
  const router = useRouter();
  const workspace = usePrimaryWorkspace();
  const { readOnly } = useWorkspaceReadOnly(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const tasks = useWorkspaceTasks(workspace.data?.id);
  const timeEntries = useWorkspaceTimeEntries(workspace.data?.id);
  const records = (timeEntries.data ?? []).filter(
    (entry) => entry.ended_at !== null,
  );
  const clientRecords = clients.data ?? [];
  const projectRecords = projects.data ?? [];
  const taskRecords = tasks.data ?? [];
  const clientById = new Map(
    clientRecords.map((client) => [client.id, client]),
  );
  const projectById = new Map(
    projectRecords.map((project) => [project.id, project]),
  );
  const taskById = new Map(taskRecords.map((task) => [task.id, task]));
  const clientIdForEntry = (entry: {
    client_id: string | null;
    project_id: string | null;
  }) =>
    entry.client_id ??
    (entry.project_id
      ? (projectById.get(entry.project_id)?.client_id ?? null)
      : null);
  const clientOptions = [
    { label: "No client", value: "" },
    ...clientRecords.map((client) => ({
      label: client.name,
      value: client.id,
    })),
  ];
  const projectOptionsForValues = (values: Record<string, string>) => [
    { label: "No project", value: "" },
    ...projectRecords
      .filter(
        (project) => !values.clientId || project.client_id === values.clientId,
      )
      .map((project) => ({ label: project.name, value: project.id })),
  ];
  const taskOptionsForValues = (values: Record<string, string>) => [
    { label: "No task", value: "" },
    ...taskRecords
      .filter((task) => {
        if (values.projectId) return task.project_id === values.projectId;
        if (!values.clientId) return true;
        const project = task.project_id
          ? projectById.get(task.project_id)
          : null;
        return project?.client_id === values.clientId;
      })
      .map((task) => ({ label: task.name, value: task.id })),
  ];
  const totalSeconds = records.reduce(
    (total, entry) => total + entry.duration_seconds,
    0,
  );
  const unbilledValue = records.reduce(
    (total, entry) => total + (entry.invoiced_at ? 0 : billableValue(entry)),
    0,
  );

  return (
    <ResourceListScreen
      title="Time Entries"
      subtitle="Review, edit, and prepare tracked work for invoicing."
      primaryAction="Add entry"
      emptyTitle="No time entries yet"
      emptyDescription="Add a manual time entry now; the live timer will write to the same table."
      toolbar={
        <Row>
          <Button
            compact
            variant="outline"
            onPress={() => router.push("/calendar" as never)}
          >
            <ButtonText>Calendar view</ButtonText>
          </Button>
        </Row>
      }
      fields={[
        {
          key: "description",
          label: "Description",
          placeholder: "Client call, design pass, coding sprint...",
        },
        {
          key: "clientId",
          label: "Client",
          placeholder: "No client",
          options: clientOptions,
          clearOnChange: ["projectId", "taskId"],
          selectTitle: "Choose client",
        },
        {
          key: "projectId",
          label: "Project",
          placeholder: "No project",
          optionsForValues: projectOptionsForValues,
          clearOnChange: ["taskId"],
          selectTitle: "Choose project",
        },
        {
          key: "taskId",
          label: "Task",
          placeholder: "No task",
          optionsForValues: taskOptionsForValues,
          selectTitle: "Choose task",
        },
        {
          key: "minutes",
          label: "Minutes",
          placeholder: "60",
          keyboardType: "numeric",
        },
        {
          key: "hourlyRate",
          label: "Hourly rate (optional)",
          placeholder: "Use matching preset",
          keyboardType: "numeric",
        },
        {
          key: "billable",
          label: "Billing",
          placeholder: "Billable",
          options: BILLABLE_OPTIONS,
          selectTitle: "Choose billing state",
        },
        {
          key: "tags",
          label: "Tags (comma-separated)",
          placeholder: "discovery, workshop",
          accessibilityLabel: "Time entry tags",
        },
      ]}
      requiredField="description"
      readOnly={readOnly}
      metrics={[
        { label: "Tracked", value: formatDuration(totalSeconds) },
        { label: "Unbilled value", value: formatMoney(unbilledValue) },
      ]}
      items={records.map((entry) => ({
        id: entry.id,
        title: entry.description ?? "Untitled time entry",
        eyebrow: new Date(entry.started_at).toLocaleDateString(),
        meta: [
          clientIdForEntry(entry)
            ? (clientById.get(clientIdForEntry(entry) ?? "")?.name ??
              "Unknown client")
            : "No client",
          entry.project_id
            ? (projectById.get(entry.project_id)?.name ?? "Unknown project")
            : "No project",
          entry.task_id
            ? (taskById.get(entry.task_id)?.name ?? "Unknown task")
            : "No task",
          formatDuration(entry.duration_seconds),
          entry.billable ? "Billable" : "Non-billable",
          entry.invoiced_at ? "Invoiced" : "Unbilled",
          entry.tags?.length
            ? `Tags: ${entry.tags.map((tag) => tag.name).join(", ")}`
            : "No tags",
        ],
        editValues:
          readOnly || entry.invoiced_at
            ? undefined
            : {
                billable: entry.billable ? "true" : "false",
                clientId: clientIdForEntry(entry) ?? "",
                description: entry.description ?? "",
                hourlyRate: entry.hourly_rate_cents
                  ? String(entry.hourly_rate_cents / 100)
                  : "",
                minutes: String(Math.round(entry.duration_seconds / 60)),
                projectId: entry.project_id ?? "",
                taskId: entry.task_id ?? "",
                tags: (entry.tags ?? []).map((tag) => tag.name).join(", "),
              },
        actions: (
          <Button
            compact
            variant="outline"
            onPress={() => router.push(`/time-entries/${entry.id}` as never)}
          >
            <ButtonText>View details</ButtonText>
          </Button>
        ),
      }))}
      loading={
        workspace.isLoading ||
        clients.isLoading ||
        projects.isLoading ||
        tasks.isLoading ||
        timeEntries.isLoading
      }
      error={
        workspace.error ??
        clients.error ??
        projects.error ??
        tasks.error ??
        timeEntries.error ??
        timeEntries.create.error ??
        timeEntries.update.error
      }
      creating={timeEntries.create.isPending}
      onCreate={(values) => timeEntries.create.mutateAsync(values)}
      updating={timeEntries.update.isPending}
      onUpdate={(id, values) => timeEntries.update.mutateAsync({ id, values })}
    />
  );
}
